import { useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, User, Shield, FileText, Palette, PenTool, Code, Megaphone, Crown, Link2Off, Image as ImageIcon, Building2, TreePine, Globe, History, ClipboardList } from "lucide-react";
import { useAuthSafe } from "./AuthContext";
import { useTemplateSafe } from "./TemplateContext";
import { ScreenshotButton } from "./ScreenshotExportModal";
import { FigmaExportButton } from "./FigmaExportModal";
import { BrokenLinksChecker } from "./BrokenLinksChecker";
import { NotificationBell } from "./NotificationBell";
import { PdfExportButton } from "./PdfExportButton";
import zapLogo from "figma:asset/f3ea20a5ac1d9a14ff41e77e5c2314b781a6a6e5.png";

/**
 * AdminBar — thin bar at the top of template pages.
 * Shows "back to dashboard" link + screenshot button + figma export + broken links checker + current user info.
 * Shows a gold "master" indicator when editing a master template.
 * Only visible when user is authenticated.
 *
 * Updated for 6-role system:
 *   admin, characterizer, designer, content_writer, developer, marketer
 */

const ROLE_LABELS: Record<string, string> = {
  admin: "\u05DE\u05E0\u05D4\u05DC",
  characterizer: "\u05DE\u05D0\u05E4\u05D9\u05D9\u05E0\u05EA",
  designer: "\u05DE\u05E2\u05E6\u05D1\u05EA",
  content_writer: "\u05DB\u05D5\u05EA\u05D1\u05EA \u05EA\u05D5\u05DB\u05DF",
  developer: "\u05DE\u05E4\u05EA\u05D7",
  marketer: "\u05DE\u05E7\u05D3\u05DE\u05EA",
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  admin: <Shield className="w-3 h-3" />,
  characterizer: <FileText className="w-3 h-3" />,
  designer: <Palette className="w-3 h-3" />,
  content_writer: <PenTool className="w-3 h-3" />,
  developer: <Code className="w-3 h-3" />,
  marketer: <Megaphone className="w-3 h-3" />,
};

const ROLE_COLORS: Record<string, string> = {
  admin: "bg-amber-400/20 text-amber-200 border-amber-400/30",
  characterizer: "bg-violet-400/20 text-violet-200 border-violet-400/30",
  designer: "bg-sky-400/20 text-sky-200 border-sky-400/30",
  content_writer: "bg-emerald-400/20 text-emerald-200 border-emerald-400/30",
  developer: "bg-orange-400/20 text-orange-200 border-orange-400/30",
  marketer: "bg-pink-400/20 text-pink-200 border-pink-400/30",
};

export function AdminBar() {
  const auth = useAuthSafe();
  const navigate = useNavigate();
  const tplCtx = useTemplateSafe();
  const [showLinkChecker, setShowLinkChecker] = useState(false);

  // Don't render if not authenticated
  if (!auth?.user) return null;

  const { user } = auth;
  const roleLabel = ROLE_LABELS[user.role] || user.role;
  const roleIcon = ROLE_ICONS[user.role];
  const roleColor = ROLE_COLORS[user.role] || ROLE_COLORS.developer;
  const isMaster = tplCtx?.isMaster || false;
  const templateId = tplCtx?.templateId;

  return (
    <>
      <nav
        data-tour-step="admin-bar"
        className="bg-zap-navy text-white text-xs py-1.5 px-4 flex items-center justify-between z-[60] relative"
        dir="rtl"
        aria-label={"\u05E1\u05E8\u05D2\u05DC \u05E0\u05D9\u05D4\u05D5\u05DC"}
      >
        {/* Right side: back to dashboard + screenshot + figma + links + pdf + notifications */}
        <div className="flex items-center gap-1">
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate("/")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/"); }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-white/10 cursor-pointer"
            aria-label={"\u05D7\u05D6\u05E8\u05D4 \u05DC\u05D3\u05E9\u05D1\u05D5\u05E8\u05D3"}
          >
            <img src={zapLogo} alt="Zap" className="w-4 h-4 rounded-sm" />
            <span style={{ fontWeight: 500 }}>{"\u05DB\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA"}</span>
            <ArrowRight className="w-3 h-3 text-white/50 mr-0.5" />
          </span>

          {/* Master template indicator */}
          {isMaster && (
            <>
              <div className="w-px h-4 bg-white/10 mx-1" />
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/20 border border-amber-400/30">
                <Crown className="w-3 h-3 text-amber-400" />
                <span className="text-amber-200" style={{ fontWeight: 600 }}>
                  {"\u05EA\u05D1\u05E0\u05D9\u05EA \u05DE\u05D0\u05E1\u05D8\u05E8"}
                </span>
              </div>
            </>
          )}

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" />

          {/* Screenshot button */}
          <ScreenshotButton />

          {/* Figma export button */}
          <FigmaExportButton />

          {/* Broken links checker button */}
          <span
            role="button"
            tabIndex={0}
            onClick={() => setShowLinkChecker(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowLinkChecker(true); }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-white/10 cursor-pointer"
            title={"\u05D1\u05D3\u05D9\u05E7\u05EA \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05E9\u05D1\u05D5\u05E8\u05D9\u05DD"}
            aria-label={"\u05D1\u05D3\u05D9\u05E7\u05EA \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05E9\u05D1\u05D5\u05E8\u05D9\u05DD"}
          >
            <Link2Off className="w-3 h-3 text-white/60" />
            <span className="hidden sm:inline" style={{ fontWeight: 500 }}>{"\u05DC\u05D9\u05E0\u05E7\u05D9\u05DD"}</span>
          </span>

          {/* Project media library */}
          {templateId && (
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/t/${templateId}/media`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/t/${templateId}/media`); }}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-white/10 cursor-pointer"
              title={"\u05DE\u05D3\u05D9\u05D4 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8"}
              aria-label={"\u05DE\u05D3\u05D9\u05D4 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8"}
            >
              <ImageIcon className="w-3 h-3 text-white/60" />
              <span className="hidden sm:inline" style={{ fontWeight: 500 }}>{"\u05DE\u05D3\u05D9\u05D4"}</span>
            </span>
          )}

          {/* PDF Export button */}
          {templateId && (
            <>
              <div className="w-px h-4 bg-white/10 mx-1" />
              <PdfExportButton
                templateId={templateId}
                className="!border-white/20 !text-white/70 hover:!text-white hover:!bg-white/10 !bg-transparent"
              />
            </>
          )}

          {/* Separator + Notification bell */}
          <div className="w-px h-4 bg-white/10 mx-1" />
          <NotificationBell dark />
        </div>

        {/* User info */}
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] ${roleColor}`} style={{ fontWeight: 600 }}>
            {roleIcon}
            {roleLabel}
          </span>
          <div className="flex items-center gap-1.5 text-gray-300">
            <User className="w-3 h-3" />
            <span>{user.displayName || user.username}</span>
          </div>
        </div>
      </nav>

      {/* Admin quick-nav sub-bar — project management links */}
      {templateId && (
        <div
          className="bg-[#0a2540] text-white/70 text-[11px] py-1 px-4 flex items-center gap-1 z-[59] relative border-t border-white/5"
          dir="rtl"
        >
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/t/${templateId}/business-info`)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/t/${templateId}/business-info`); }}
            className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <Building2 className="w-3 h-3" />
            {"\u05E8\u05E7\u05E2 \u05E2\u05DC \u05D4\u05E2\u05E1\u05E7"}
          </span>
          <span className="text-white/15">|</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/t/${templateId}/qa-checklist`)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/t/${templateId}/qa-checklist`); }}
            className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <ClipboardList className="w-3 h-3" />
            {"\u05E6\u05F3\u05E7\u05DC\u05D9\u05E1\u05D8 QA"}
          </span>
          <span className="text-white/15">|</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/t/${templateId}/site-tree`)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/t/${templateId}/site-tree`); }}
            className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <TreePine className="w-3 h-3" />
            {"\u05E2\u05E5 \u05D0\u05EA\u05E8"}
          </span>
          <span className="text-white/15">|</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/t/${templateId}/seo-manager`)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/t/${templateId}/seo-manager`); }}
            className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <Globe className="w-3 h-3" />
            {"\u05E0\u05D9\u05D4\u05D5\u05DC SEO"}
          </span>
          <span className="text-white/15">|</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/t/${templateId}/change-history`)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/t/${templateId}/change-history`); }}
            className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <History className="w-3 h-3" />
            {"\u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D4"}
          </span>
        </div>
      )}

      {/* Broken links checker modal */}
      {showLinkChecker && (
        <BrokenLinksChecker onClose={() => setShowLinkChecker(false)} />
      )}
    </>
  );
}